import { ReadingMessage } from "@/entities/ReadingMessage";

import { BaseProfile } from "../types";

export const genderOptions = [
  { label: "Female", value: "WOM" },
  { label: "Male", value: "MAN" },
  { label: "Non-Binary", value: "NON" },
  { label: "Prefer not to say", value: "NUL" },
];

export const experienceOptions = [
  { label: "None", value: "0" },
  { label: "Low", value: "1" },
  { label: "Medium", value: "2" },
  { label: "High", value: "3" },
];

export const seenWriterOptions = [
  { label: "Yes", value: "true" },
  { label: "No", value: "false" },
];

export const birthTimeOptions = [
  { label: "12:00 AM", value: "00:00:00" },
  { label: "1:00 AM", value: "01:00:00" },
  { label: "2:00 AM", value: "02:00:00" },
  { label: "3:00 AM", value: "03:00:00" },
  { label: "4:00 AM", value: "04:00:00" },
  { label: "5:00 AM", value: "05:00:00" },
  { label: "6:00 AM", value: "06:00:00" },
  { label: "7:00 AM", value: "07:00:00" },
  { label: "8:00 AM", value: "08:00:00" },
  { label: "9:00 AM", value: "09:00:00" },
  { label: "10:00 AM", value: "10:00:00" },
  { label: "11:00 AM", value: "11:00:00" },
  { label: "12:00 PM", value: "12:00:00" },
  { label: "1:00 PM", value: "13:00:00" },
  { label: "2:00 PM", value: "14:00:00" },
  { label: "3:00 PM", value: "15:00:00" },
  { label: "4:00 PM", value: "16:00:00" },
  { label: "5:00 PM", value: "17:00:00" },
  { label: "6:00 PM", value: "18:00:00" },
  { label: "7:00 PM", value: "19:00:00" },
  { label: "8:00 PM", value: "20:00:00" },
  { label: "9:00 PM", value: "21:00:00" },
  { label: "10:00 PM", value: "22:00:00" },
  { label: "11:00 PM", value: "23:00:00" },
];

export const planOptions = [
  { label: "All Plans", value: "" },
  { label: "Yearly", value: "year" },
  { label: "Monthly", value: "month" },
];

export const lastActiveOptions = [
  { label: "All Time", value: 0 },
  { label: "Last Day", value: 1 },
  { label: "Last Week", value: 7 },
  { label: "Last Month", value: 30 },
];

export function mapHopesToOptions(hopes: unknown[]) {
  if (!Array.isArray(hopes)) {
    return [];
  }

  return hopes.map(({ id, hope }) => ({
    value: `${id}`,
    label: hope,
  }));
}

export function mapApiHopesToFormData(hopes) {
  return hopes.map(({ id }) => `${id}`);
}

// TODO: avoid this
export function mapProfileToFormData(profile: BaseProfile & { birthDate: string }) {
  profile.birthDate = toDateString(new Date(profile.birthDate));
  profile.sibylHopes = profile.sibylHopes ? mapApiHopesToFormData(profile.sibylHopes) : [];
  profile.experienceLevel = `${profile.experienceLevel}`;
  return profile;
}

export function toDateString(date: Date) {
  if (!(date instanceof Date)) {
    return "Invalid Date";
  }

  return date.toLocaleDateString("en-US", { timeZone: "UTC" });
}

export function formatDate(date: Date) {
  if (!(date instanceof Date)) {
    return "Invalid Date";
  }

  let month = `${date.getMonth() + 1}`;
  let day = `${date.getDate()}`;
  const year = date.getFullYear();

  if (month.length < 2) {
    month = `0${month}`;
  }
  if (day.length < 2) {
    day = `0${day}`;
  }

  return [year, month, day].join("-");
}

export function toDateWithTime(date: Date) {
  if (!(date instanceof Date)) {
    return "Invalid Date";
  }

  const min = date.getMinutes();
  const minStr = min < 10 ? `0${min}` : `${min}`;
  return `${toDateString(date)} at ${date.getHours()}:${minStr}`;
}

export function compareStringDates(date1: string | null, date2: string | null) {
  const nullCompare = comparePossibleNull(date1, date2);
  if (nullCompare !== null) {
    return nullCompare;
  }
  const dateObj1 = new Date(date1);
  const dateObj2 = new Date(date2);

  if (dateObj1 > dateObj2) {
    return -1;
  }
  if (dateObj1 < dateObj2) {
    return 1;
  }
  return 0;
}

export function compareNullStrings(string1: string | null, string2: string | null) {
  const nullCompare = comparePossibleNull(string1, string2);
  if (nullCompare !== null) {
    return nullCompare;
  }
  return string1.localeCompare(string2);
}

function comparePossibleNull(val1: unknown, val2: unknown) {
  if (!val1 && !val2) {
    return 0;
  }
  if (!val1) {
    return 1;
  }
  if (!val2) {
    return -1;
  }
  return null;
}

export function cropText(val: string, maxLen: number): string {
  if (typeof val !== "string") {
    return "";
  }
  if (typeof maxLen !== "number") {
    return val;
  }
  if (val.length > maxLen) {
    return `${val.substring(0, maxLen).trimEnd()}...`;
  }
  return val;
}

export function getFullZodiac(zodiac: string): string {
  let zodiacName = "";
  switch (zodiac) {
    case "ARI":
      zodiacName = "ARIES";
      break;
    case "TAE":
      zodiacName = "TAURUS";
      break;
    case "GEM":
      zodiacName = "GEMINI";
      break;
    case "CAN":
      zodiacName = "CANCER";
      break;
    case "LEO":
      zodiacName = "LEO";
      break;
    case "VIR":
      zodiacName = "VIRGO";
      break;
    case "LIB":
      zodiacName = "LIBRA";
      break;
    case "SCO":
      zodiacName = "SCORPIO";
      break;
    case "SAG":
      zodiacName = "SAGITTARIUS";
      break;
    case "CAP":
      zodiacName = "CAPRICORN";
      break;
    case "AQU":
      zodiacName = "AQUARIUS";
      break;
    case "PIS":
      zodiacName = "PISCES";
      break;
  }

  return zodiacName;
}

export function sortByDate<T extends Record<string, unknown>>(val: T[], field: keyof T & string): T[] {
  const result = [...val];

  return result.sort(
    (a, b) => Number(new Date((b as Record<string, any>)[field])) - Number(new Date((a as Record<string, any>)[field]))
  );
}

export const parseJSON = (data) => {
  let result = null;
  try {
    result = JSON.parse(data);
  } catch (e) {
    console.error("Invalid JSON format");
  }
  return result;
};

export function isConsecutive(messages: ReadingMessage[], i: number, isReversed?: boolean) {
  const currElem = messages[i];
  const nextElem = isReversed ? messages[i - 1] : messages[i + 1];

  const checkLastElem = isReversed ? false : i === messages.length - 1;

  if (checkLastElem || currElem?.sender.id !== nextElem?.sender.id) {
    return false;
  }

  const thisTime = new Date(currElem?.createdAt).getTime();
  const nextTime = new Date(nextElem?.createdAt).getTime();

  return nextTime - thisTime < 3600000;
}

export function filterUniqueMessages(messages: ReadingMessage[]) {
  if (!Array.isArray(messages)) {
    return [];
  }

  const idSet = new Set();
  const uniqueMessages = [];

  messages.forEach((message) => {
    if (!idSet.has(message.id)) {
      uniqueMessages.push(message);
      idSet.add(message.id);
    }
  });

  return uniqueMessages;
}

export function camelToSnakeCase(data: any): any {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k.replace(/[A-Z]/g, (l) => `_${l.toLowerCase()}`), v])
    );
  }
  if (Array.isArray(data)) {
    return data.map(camelToSnakeCase);
  }
  return data;
}

export function snakeToCamelCase(data: any): any {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return Object.fromEntries(
      Object.entries(data).map(([k, v]) => {
        if (v && typeof v === "object" && !Array.isArray(v)) {
          v = snakeToCamelCase(v);
        } else if (Array.isArray(v)) {
          v = v.map(snakeToCamelCase);
        }
        return [k.replace(/_([a-z])/gi, (_m, l) => l.toUpperCase()), v];
      })
    );
  }
  if (Array.isArray(data)) {
    return data.map(snakeToCamelCase);
  }
  return data;
}

export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
};

export function getFancyTime(date: string | number): string {
  if (!date) {
    return "Invalid Date";
  }

  const parsed = new Date(date);

  return `${parsed.toLocaleString("default", { month: "long" })} ${parsed.getDate()}, ${parsed.getFullYear()}`;
}
